import { Box, CircularProgress, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import DashboardHeader from "../../components/dashboard-header";
import { useNotification } from "../../contexts/NotificationContext";
import { useGetIdeaBooks } from "../../hooks/idea-books/use-get-idea-books";
import type { IdeaBook } from "../../hooks/idea-books/types";
import { useUserContext } from "../../hooks/userContext";
import { Pages } from "../../utils";

export const MyIdeaBooksPage = () => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { showError } = useNotification();
  const { getIdeaBooks } = useGetIdeaBooks();
  const [ideaBooks, setIdeaBooks] = useState<IdeaBook[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const homeownerId = user?.id;
    if (!homeownerId) {
      setIdeaBooks([]);
      setIsLoading(false);
      return;
    }

    let isActive = true;
    setIsLoading(true);
    getIdeaBooks(homeownerId, true)
      .then((books) => {
        if (!isActive) return;
        setIdeaBooks(books);
      })
      .catch(() => {
        if (!isActive) return;
        showError("Unable to load idea books. Please try again.");
      })
      .finally(() => {
        if (!isActive) return;
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [getIdeaBooks, showError, user?.id]);

  return (
    <Box sx={{ backgroundColor: "#F8F8F8", minHeight: "100vh" }}>
      <DashboardHeader
        title="My Idea Books"
        showBackButton={false}
        primaryAction={{
          label: "View Designer Files",
          onClick: () => navigate(Pages.DESIGNER_FILES_HOMEOWNER),
          variant: "contained",
        }}
        primaryActionSx={{
          backgroundColor: "#111827",
          color: "#fff",
          "&:hover": { backgroundColor: "#0F172A" },
        }}
      />

      <Box sx={{ px: { xs: 2, md: 3 }, pb: 6 }}>
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "18px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
            p: { xs: 2.5, md: 3.5 },
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress sx={{ color: "#C2185B" }} />
            </Box>
          ) : ideaBooks.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                No idea books yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Save your favorite designer file images to start an idea book.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: {
                  xs: "repeat(1, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                  xl: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              {ideaBooks.map((book) => {
                const coverUrl =
                  book.coverImage?.url ?? book.coverImage?.path ?? "";
                const itemCountLabel = `${book.itemCount} idea${
                  book.itemCount === 1 ? "" : "s"
                }`;

                return (
                  <Box
                    key={book._id}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate(
                        Pages.MY_IDEA_BOOK_DETAILS.replace(
                          ":ideaBookId",
                          book._id
                        )
                      )
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(
                          Pages.MY_IDEA_BOOK_DETAILS.replace(
                            ":ideaBookId",
                            book._id
                          )
                        );
                      }
                    }}
                    sx={{
                      borderRadius: "16px",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      cursor: "pointer",
                      outline: "none",
                      "&:focus-visible": {
                        boxShadow:
                          "0 0 0 3px rgba(194, 24, 91, 0.2), 0 2px 8px rgba(0, 0, 0, 0.08)",
                      },
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      {coverUrl ? (
                        <Box
                          component="img"
                          src={coverUrl}
                          alt={book.name}
                          loading="lazy"
                          sx={{
                            width: "100%",
                            height: 180,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 180,
                            backgroundColor: "#F3F4F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ImageOutlinedIcon sx={{ color: "#9CA3AF" }} />
                        </Box>
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          left: 12,
                          bottom: 12,
                          px: 1.2,
                          py: 0.4,
                          borderRadius: "999px",
                          backgroundColor: "rgba(17, 24, 39, 0.85)",
                          color: "#fff",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <ImageOutlinedIcon sx={{ fontSize: 14 }} />
                        {book.itemCount}
                      </Box>
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: "#111827",
                          mb: 0.5,
                        }}
                      >
                        {book.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {itemCountLabel}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
